/*
 * renderer.js — turns a viseme track into actual pixels.
 *
 * Two things the engine deliberately does not do:
 *   1. MORPH between shapes instead of cutting. At ~12 changes/sec a hard cut
 *      reads as mechanical. We interpolate the signed distance fields of the
 *      outgoing and incoming shapes, which yields genuine intermediate mouth
 *      shapes -- still 1-bit, still crisp, no brightness fading.
 *   2. Pick an OPENNESS LEVEL from loudness, so a shouted "aa" is wider than a
 *      muttered one.
 *
 * Zero dependencies. Real arithmetic only. Reference for the Swift/Kotlin ports.
 */
const RN = 48, RNP = RN * RN;   // prefixed: this file gets inlined alongside others

/* Chamfer distance transform. dist[i] = distance to the nearest set pixel. */
function distance(bits, invert) {
  const d = new Float32Array(RNP);
  for (let i = 0; i < RNP; i++) {
    const on = invert ? !bits[i] : !!bits[i];
    d[i] = on ? 0 : 1e6;
  }
  const D1 = 1, D2 = 1.41421356;
  for (let y = 0; y < RN; y++) for (let x = 0; x < RN; x++) {
    const i = y * RN + x; let v = d[i];
    if (x > 0)            v = Math.min(v, d[i - 1] + D1);
    if (y > 0)            v = Math.min(v, d[i - RN] + D1);
    if (x > 0 && y > 0)   v = Math.min(v, d[i - RN - 1] + D2);
    if (x < RN-1 && y > 0) v = Math.min(v, d[i - RN + 1] + D2);
    d[i] = v;
  }
  for (let y = RN - 1; y >= 0; y--) for (let x = RN - 1; x >= 0; x--) {
    const i = y * RN + x; let v = d[i];
    if (x < RN-1)             v = Math.min(v, d[i + 1] + D1);
    if (y < RN-1)             v = Math.min(v, d[i + RN] + D1);
    if (x < RN-1 && y < RN-1)  v = Math.min(v, d[i + RN + 1] + D2);
    if (x > 0 && y < RN-1)    v = Math.min(v, d[i + RN - 1] + D2);
    d[i] = v;
  }
  return d;
}
/* signed: negative inside the shape, positive outside */
function sdf(bits) {
  const out = distance(bits, false), inn = distance(bits, true);
  const s = new Float32Array(RNP);
  for (let i = 0; i < RNP; i++) s[i] = bits[i] ? -inn[i] : out[i];
  return s;
}

/* SDF cache keyed on the face asset, not the renderer instance. 64 avatars of
   the same character previously held 64 identical copies (25 MB); now they
   share one (0.4 MB). */
const SDF_CACHE = new WeakMap();
function cacheFor(face) {
  let c = SDF_CACHE.get(face);
  if (!c) { c = new Map(); SDF_CACHE.set(face, c); }
  return c;
}

/* Deterministic blink schedule. Must be a pure function of time: a renderer
   that mutates blink state inside frame() cannot be scrubbed, re-rendered or
   reproduced, and frame(5.0) stops matching frame(5.0). */
function hash32(n) {
  n = (n ^ 61) ^ (n >>> 16); n = (n + (n << 3)) | 0;
  n ^= n >>> 4; n = Math.imul(n, 0x27d4eb2d); n ^= n >>> 15;
  return n >>> 0;
}
function blinkingAt(t, seed, period, dur) {
  const n = Math.floor(t / period);
  for (let k = n - 1; k <= n; k++) {           // check the previous slot too
    const start = k * period + (hash32(k ^ seed) % 1000) / 1000 * (period - dur);
    if (t >= start && t < start + dur) return true;
  }
  return false;
}

export class FaceRenderer {
  /** @param face  { eyes, mouths, mouthLevels? }  arrays of 2304 0/1
   *  @param opts  { blendMs = 45, levels = true, blink = true } */
  constructor(face, opts = {}) {
    this.face = face;
    this.blendMs = opts.blendMs ?? 80;      // upper bound
    this.blendFrac = opts.blendFrac ?? 0.7;  // ...or this share of the hold
    /* Share of the blend that happens BEFORE the event time.
       0   = reactive: the mouth starts moving when the sound starts.
       0.6 = anticipatory: the mouth is already moving into the shape, and
             lands on it as the sound arrives - which is how real speech and
             hand-keyed animation both work. */
    this.anticipate = opts.anticipate ?? 0;
    this.useLevels = (opts.levels ?? true) && !!face.mouthLevels;
    this.blink = opts.blink ?? true;
    this.sdfCache = cacheFor(face);          // shared across instances
    this.out = new Uint8Array(RNP);
    this.blinkSeed = opts.blinkSeed ?? 1;    // same seed -> same performance
    this.blinkPeriod = opts.blinkPeriod ?? 4.0;
    this.blinkDur = opts.blinkDur ?? 0.11;
  }

  _shape(viseme, level) {
    if (this.useLevels) {
      const L = this.face.mouthLevels[viseme];
      if (L) return L[String(level)] || L['2'];
    }
    return this.face.mouths[viseme] || this.face.mouths.sil;
  }
  _sdf(viseme, level) {
    const key = viseme + '/' + level;
    let s = this.sdfCache.get(key);
    if (!s) { s = sdf(this._shape(viseme, level)); this.sdfCache.set(key, s); }
    return s;
  }

  /** index of the newest event with t <= time */
  static seek(track, time) {
    let lo = 0, hi = track.length - 1, k = 0;
    while (lo <= hi) { const m = (lo + hi) >> 1; if (track[m].t <= time) { k = m; lo = m + 1; } else hi = m - 1; }
    return k;
  }

  /** Binary 48x48 face at `time` seconds. */
  frame(track, time) {
    const o = this.out; o.fill(0);
    const shut = this.blink && blinkingAt(time, this.blinkSeed, this.blinkPeriod, this.blinkDur);
    const eye = this.face.eyes[shut ? 'BLINK' : 'NEUTRAL'];
    if (eye) for (let i = 0; i < RNP; i++) if (eye[i]) o[i] = 1;

    if (!track.length) return o;
    const k = FaceRenderer.seek(track, time);
    const cur = track[k];
    const lvl = this.useLevels ? Math.max(1, Math.min(3, cur.level || 2)) : 2;

    /* Find the transition whose blend window contains `time`. With anticipation
       the window straddles the event, so the NEXT transition may already be
       under way - check it first. */
    for (const j of [k + 1, k]) {
      if (j <= 0 || j >= track.length) continue;
      const ev = track[j], prev = track[j - 1];
      const elvl = this.useLevels ? Math.max(1, Math.min(3, ev.level || 2)) : 2;
      const plvl = this.useLevels ? Math.max(1, Math.min(3, prev.level || 2)) : 2;
      if (prev.viseme === ev.viseme && plvl === elvl) continue;
      const holdAfter  = ((j + 1 < track.length ? track[j + 1].t : ev.t + 0.2) - ev.t) * 1000;
      const holdBefore = (ev.t - prev.t) * 1000;
      const blend = Math.min(this.blendMs, Math.min(holdAfter, holdBefore) * (this.blendFrac ?? 0.7));
      if (blend <= 8) continue;
      const start = ev.t - (blend * this.anticipate) / 1000;
      const end   = ev.t + (blend * (1 - this.anticipate)) / 1000;
      if (time >= start && time < end) {
        const t = (time - start) / (end - start);
        const e = t * t * (3 - 2 * t);
        const a = this._sdf(prev.viseme, plvl), b = this._sdf(ev.viseme, elvl);
        for (let i = 0; i < RNP; i++) if (a[i] + (b[i] - a[i]) * e <= 0) o[i] = 1;
        return o;
      }
    }

    const m = this._shape(cur.viseme, lvl);
    for (let i = 0; i < RNP; i++) if (m[i]) o[i] = 1;
    return o;
  }
}
export default FaceRenderer;
